import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { BLOB_BUCKETS_PER_APP_MAX, allowedLocationsSchema, appAdoptionStateSchema, blobBucketNameSchema, dataLocationSchema } from "@zion/sdk-contracts";
//#region src/app-adoptions.ts
/**
* `/v1/admin/app-adoptions`: the tenant admin's accept, pause, resume and remove of each app the CP
* licensed to the org (plan `2026-10-06-tenant-app-adoption`, C-2, D-2, D-6, D-11).
*
* The adoption state is a column of core-api's `release.org_app_entitlements` row. The app is live
* for the org only in `accepted` (`isLiveAppAdoptionState` of `@zion/sdk-contracts`). The states,
* the actions and the state machine live in `@zion/sdk-contracts` `app-adoption-state.ts`; this
* module holds the routes, the wire shapes and the refusal codes.
*
* Producer: core-api's `app-adoptions` controller in `cloud/dp/core-api/src/admin/` (WS-2a).
* Consumers: the org admin app in `witness-ai/zion-admin-app`, and core-api's
* `admin-route-census.spec.ts`, which checks each route against `openapi.json`.
*
* Authorization. The controller carries a class-level
* `@RequireOrgPermission('app-permission-grant:manage')`, so every route here, the list included,
* needs that permission (index defaults table). The org comes from the authenticated principal
* only. No route takes an org id in its path, query or body, and `{appId}` names an app inside the
* caller's org. Every read and write runs under the caller's org through RLS.
*
* Kernel class (`CONTRACT_ROUTE_KERNEL_CLASS` in `route-census.ts`), right `authority.grants`
* (D-11): the list at tier 0, accept and resume at tier 1 (they give an app access to org data, as
* a kernel consent does), pause and remove at tier 2 (they only take access away). The locations
* read of 2026-10-13-blob-buckets R-119 item 4 takes the class of the list, tier 0. The six routes
* sit in the admin fold `ADMIN_CONTRACT_ROUTES`.
*
* The refusals of the four write routes, after the guards (400 bad `appId`, 401, 403, the kernel
* step-up), in this order:
*
* 1. 404 `app_adoption_not_found`: the caller's org holds no entitlement row for `appId`, or the row
*    is the row of a library instance (a catalog slug that starts with `zl-`,
*    `isReservedInstanceSlug` of `@zion/sdk-contracts`). The zion-operator owns the adoption state
*    of an instance row, so no tenant admin adopts an instance (2026-10-12-shared-libraries, R-012,
*    R-013). Another org's app answers the same, so the route is no existence oracle.
* 2. 409 `app_licence_retracted`: the CP retracted the org's licence for the app. The licence
*    outranks every adoption state, so every action refuses.
* 3. 409 `app_standing`: `pause` or `remove` of a standing app, a slug of `FIRST_PARTY_APPS` (D-4).
*    `accept` and `resume` of a standing app pass on to the state machine.
* 4. 409 `app_adoption_state_conflict`: `appAdoptionTransition` answers `conflict`, for example a
*    `resume` of a removed app.
*
* Then the write. A `changed` transition stores the new state and emits the action's audit event
* (`APP_ADOPTION_AUDIT_EVENT`) in the same transaction, and answers 200 with the row. An
* `unchanged` transition stores nothing, emits no audit event, and answers 200 with the row as it
* is.
*
* Direction rule (R-28): the request body schema is `z.strictObject` and refuses an unknown key;
* each response schema is `z.object` and strips one. No schema here carries a `.transform()`, so
* `createOpenApiDocument()` can represent each one.
*/
/** The collection path. It has no parameter, so the OpenAPI form and the request form agree. */
const ADMIN_APP_ADOPTIONS_PATH = `${ADMIN_API_PREFIX}/app-adoptions`;
/**
* One app's adoption, in OpenAPI `{param}` form. No route answers on this path alone; the four
* actions hang below it. Fill an action path with {@link adminAppAdoptionActionPath}.
*/
const ADMIN_APP_ADOPTION_PATH_TEMPLATE = `${ADMIN_APP_ADOPTIONS_PATH}/{appId}`;
/**
* `GET /v1/admin/app-adoptions`. Status 200 with {@link listAppAdoptionsResponseSchema}: one row
* per entitlement row of the caller's org whose admin veto `revoked_at` is unset, licence-retracted
* rows included, ordered by `appSlug` ascending in byte order (`COLLATE "C"`). The list skips the
* row of a library instance, a catalog slug that starts with `zl-` (`isReservedInstanceSlug` of
* `@zion/sdk-contracts`): the zion-operator owns its adoption state (2026-10-12-shared-libraries,
* R-012, R-013). An org with no row gets `[]`. No query. Kernel class `authority.grants` tier 0. core-api reports a row with no stored
* state as `accepted`, the expand-phase rule of migration `release/0020` (D-9): it maps the column
* through `appAdoptionStateFromColumn` of `@zion/sdk-contracts`, never straight into
* `isLiveAppAdoptionState`.
*/
const LIST_APP_ADOPTIONS_ROUTE = route("GET", ADMIN_APP_ADOPTIONS_PATH);
/** The path of the locations read. It has no parameter, so the OpenAPI form and the request form agree. */
const ADMIN_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_PATH = `${ADMIN_APP_ADOPTIONS_PATH}/blob-bucket-locations`;
/**
* `GET /v1/admin/app-adoptions/blob-bucket-locations` (2026-10-13-blob-buckets R-119 item 4). Status
* 200 with {@link appAdoptionBlobBucketLocationsResponseSchema}: the allowed list of this data plane,
* the values that the `bucketLocations` of {@link ACCEPT_APP_ADOPTION_ROUTE} may name. The keys it
* may name are each row's {@link appAdoptionSchema} `orgBlobBuckets`. No query, no body. Kernel class
* `authority.grants` tier 0, the class of the list: the org admin who accepts holds that right and
* can lack `tenant.settings`.
*
* The list is data-plane wide, the same for every org and every app. The control plane owns it
* (`platform.data_placements.allowed_locations`), and core-api answers its stored copy,
* `release.dp_status.allowed_locations`. `allowedLocations` is `null` while the data plane holds no
* list (no row, or a NULL column). An accept that then names a `bucketLocations` key answers 409
* `blob_bucket_locations_unknown`, so the admin app offers no pick. An accept with no pick still
* passes.
*/
const GET_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_ROUTE = route("GET", ADMIN_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_PATH);
/**
* `POST /v1/admin/app-adoptions/{appId}/accept`. Body {@link appAdoptionAcceptBodySchema}, its own
* schema (2026-10-13-blob-buckets R-071 item 7). Status 200 with
* {@link appAdoptionActionResponseSchema}. Legal from `offered` and `removed`; `accepted` answers
* unchanged; `paused` answers 409 `app_adoption_state_conflict` (a paused app takes resume). Audit
* event `app_adoption.accepted`. Kernel class `authority.grants` tier 1.
*
* The bucket location pick (R-069 item 1, R-071 items 4 and 7). core-api runs a pick step beside
* the consent step, in the same transaction, for every accept that names a non-empty
* `bucketLocations`, the `unchanged` accept included. It upserts one row of
* `release.org_blob_bucket_settings` per key. Its refusals, with the codes of
* `BLOB_BUCKET_ERROR_CODES` in `blob-buckets.ts`:
*
* - 400 `blob_bucket_not_declared`: a key that is not a `scope: org`, `locality: central` bucket
*   of the app's primary manifest;
* - 400 `blob_bucket_location_not_allowed`: a value outside the data plane's allowed list;
* - 409 `blob_bucket_locations_unknown`: the data plane holds no allowed list yet;
* - 409 `blob_bucket_location_locked`: a key whose bucket exists already (`bound_at` is set).
*
* An accept with no `bucketLocations`, or with `{}`, runs no pick step. The pick step gives none
* of these four refusals to it, also before the first facts arrive (R-071 item 4). The consent
* step below runs on every accept, a `{}` accept included.
*
* The shared bucket consent (2026-10-13-blob-buckets R-094 item 6, R-096 item 2). An accept of an
* app with a `scope: global` bucket is also the org's consent to that bucket: one bucket per app in
* the control plane project, which every org that accepted the app shares. core-api knows of such a
* bucket from two sources: the app's synced primary manifest, read once with the read of the pick
* step, and the list that the control plane's app offer carried (`AppOffer.sharedBlobBuckets` of
* `@zion/sdk-cp-dp-contracts`), which core-api stores per app and returns as
* `appAdoptionSchema.sharedBlobBuckets`. It refuses:
*
* - 400 `blob_bucket_shared_consent_required`: the manifest or the stored list names a `global`
*   bucket, and the body does not hold `sharedBucketConsent: true`;
* - 409 `blob_bucket_locations_unknown`: the manifest read fails, the stored list names no
*   `global` bucket, and the body does not hold `sharedBucketConsent: true`, so core-api cannot
*   tell whether the app needs the consent. The read fails when the primary manifest row is not a
*   JSON object, or when its `blobStorage` fails `appBlobStorageSchema`. It fails also when other
*   rows supply buckets: the primary row can declare a `global` bucket that no other row shows.
*
* A manifest that has not reached the data plane yet, with no stored list, is no refusal: the
* offer named no shared bucket, so a `{}` accept passes as it did before stage 5. So an `accepted`
* row of an app with a `global` bucket is always a consented row, and the control plane reads
* `accepted` as the consent. Pause takes the grant away, resume returns it, and remove
* ends the consent; no other route withdraws it. For an app with no `global` bucket, core-api
* ignores `sharedBucketConsent`. The audit event `app_adoption.accepted` of an app with a `global`
* bucket carries `sharedBucketConsent`.
*/
const ACCEPT_APP_ADOPTION_ROUTE = route("POST", `${ADMIN_APP_ADOPTION_PATH_TEMPLATE}/accept`);
/**
* `POST /v1/admin/app-adoptions/{appId}/pause`. Body {@link appAdoptionActionBodySchema}. Status 200
* with {@link appAdoptionActionResponseSchema}. Legal from `accepted`; `paused` answers unchanged;
* `offered` and `removed` answer 409 `app_adoption_state_conflict`; a standing app answers 409
* `app_standing`. Audit event `app_adoption.paused`. Kernel class `authority.grants` tier 2.
*/
const PAUSE_APP_ADOPTION_ROUTE = route("POST", `${ADMIN_APP_ADOPTION_PATH_TEMPLATE}/pause`);
/**
* `POST /v1/admin/app-adoptions/{appId}/resume`. Body {@link appAdoptionActionBodySchema}. Status 200
* with {@link appAdoptionActionResponseSchema}. Legal from `paused`; `accepted` answers unchanged;
* `offered` and `removed` answer 409 `app_adoption_state_conflict` (they take accept). Audit event
* `app_adoption.resumed`. Kernel class `authority.grants` tier 1.
*/
const RESUME_APP_ADOPTION_ROUTE = route("POST", `${ADMIN_APP_ADOPTION_PATH_TEMPLATE}/resume`);
/**
* `POST /v1/admin/app-adoptions/{appId}/remove`. Body {@link appAdoptionActionBodySchema}. Status 200
* with {@link appAdoptionActionResponseSchema}. Legal from `offered`, `accepted` and `paused`;
* `removed` answers unchanged; a standing app answers 409 `app_standing`. A `changed` remove also
* runs the remove cuts of WS-2b in the same transaction (D-6). Audit event `app_adoption.removed`.
* Kernel class `authority.grants` tier 2.
*/
const REMOVE_APP_ADOPTION_ROUTE = route("POST", `${ADMIN_APP_ADOPTION_PATH_TEMPLATE}/remove`);
/**
* The write route of each action, frozen: `accept`, `pause`, `resume` and `remove` to the four route
* constants above.
*/
const APP_ADOPTION_ACTION_ROUTES = Object.freeze({
	accept: ACCEPT_APP_ADOPTION_ROUTE,
	pause: PAUSE_APP_ADOPTION_ROUTE,
	resume: RESUME_APP_ADOPTION_ROUTE,
	remove: REMOVE_APP_ADOPTION_ROUTE
});
/**
* The body of the pause, resume and remove routes: the strict empty object `{}`. Any key fails, so
* a body that names an org, a state or an app is refused before the handler runs. The accept route
* takes {@link appAdoptionAcceptBodySchema} (2026-10-13-blob-buckets R-071 item 7).
*/
const appAdoptionActionBodySchema = z.strictObject({});
/**
* The body of {@link ACCEPT_APP_ADOPTION_ROUTE} (2026-10-13-blob-buckets R-069 item 1, R-071
* item 7, R-094 item 6): `{ bucketLocations?, sharedBucketConsent? }`.
*
* - `bucketLocations`: optional. A record from a logical bucket name of the app
*   (`blobBucketNameSchema` of `@zion/sdk-contracts`) to the location the org admin picks for that
*   bucket (`dataLocationSchema`: a single region; the schema refuses `us` and `eu`). At most
*   `BLOB_BUCKETS_PER_APP_MAX` (16) keys, the most buckets one app declares. A bucket with no key
*   gets the org data location.
* - `sharedBucketConsent`: optional, and the one value it admits is `true`. The org admin sends it
*   when the app declares a `scope: global` bucket, after the admin app shows that the org's data in
*   that bucket sits beside the data of every other org that accepted the app. The schema refuses
*   `false` with the other non-`true` values, so a consent is always an explicit `true` and its
*   absence is the one form of "no consent". core-api requires it for an app with a `global` bucket and
*   ignores it for every other app (see {@link ACCEPT_APP_ADOPTION_ROUTE}).
*
* `{}` parses, so an admin app built before stage 3b keeps its accept of an app with no `global`
* bucket. When the manifest read fails and the stored list names no `global` bucket, core-api
* answers 409 `blob_bucket_locations_unknown` to a body with no `sharedBucketConsent: true`
* (R-094 item 6, R-096 item 2, see {@link ACCEPT_APP_ADOPTION_ROUTE}). `{ bucketLocations: {} }`
* also parses and picks nothing. The schema checks the shape alone: core-api checks each key
* against the app's declarations and each value against the allowed list (see
* {@link ACCEPT_APP_ADOPTION_ROUTE}).
*
* Refusals, each a 400 from core-api's strict pipe before the handler runs: an unknown key (an org,
* a state, `maxSizeGb`); a key outside the bucket name grammar, at `['bucketLocations', key]`; a
* value outside the location grammar, at `['bucketLocations', key]`; more than 16 keys, at
* `['bucketLocations']`, message `at most 16 buckets`; a `sharedBucketConsent` that is not `true`
* (`false`, `'true'`, `1`, `null`), at `['sharedBucketConsent']`. No `.transform()`.
*/
const appAdoptionAcceptBodySchema = z.strictObject({
	bucketLocations: z.record(blobBucketNameSchema, dataLocationSchema).refine((picks) => Object.keys(picks).length <= BLOB_BUCKETS_PER_APP_MAX, { message: `at most ${BLOB_BUCKETS_PER_APP_MAX} buckets` }).optional(),
	sharedBucketConsent: z.literal(true).optional()
});
/**
* One list of logical bucket names on {@link appAdoptionSchema}: `sharedBlobBuckets`
* (2026-10-13-blob-buckets R-096 item 3) and `orgBlobBuckets` (R-119 item 2). 1 to
* `BLOB_BUCKETS_PER_APP_MAX` (16) names, each in `blobBucketNameSchema`, strictly ascending in byte
* order, so sorted and distinct. A repeat of the name before it fails at its index with
* `duplicate logical bucket name`; a name below the name before it fails at its index with
* `logical bucket names must be sorted in byte order`. Module-private: the row schema is the one
* carrier. No `.transform()`. Pure.
*/
const logicalBucketNamesSchema = z.array(blobBucketNameSchema).min(1).max(BLOB_BUCKETS_PER_APP_MAX).superRefine((names, ctx) => {
	let previous = null;
	names.forEach((name, i) => {
		if (previous !== null && name === previous) ctx.addIssue({
			code: "custom",
			path: [i],
			message: "duplicate logical bucket name"
		});
		else if (previous !== null && name < previous) ctx.addIssue({
			code: "custom",
			path: [i],
			message: "logical bucket names must be sorted in byte order"
		});
		previous = name;
	});
});
/**
* One app's adoption row for the caller's org:
*
* - `appId`: the `release.apps_catalog` uuid, the value {@link adminAppAdoptionActionPath} takes;
* - `appSlug`: the app's catalog slug, a non-empty text;
* - `displayName`: the app's catalog name, a non-empty text;
* - `state`: one of the four adoption states (`appAdoptionStateSchema`); a fifth value fails;
* - `standing`: `true` when the slug is in `FIRST_PARTY_APPS`, so pause and remove answer 409
*   `app_standing`;
* - `licenceRetracted`: `true` when the CP retracted the org's licence (`licence_retracted_at` is
*   set); every action then answers 409 `app_licence_retracted`, and the app is not live in any state;
* - `updatedAt`: an ISO 8601 UTC time. It is the time of the last state write by an action, and the
*   row's `granted_at` while no action has written the state;
* - `sharedBlobBuckets`: optional (2026-10-13-blob-buckets R-096 item 3, R-119 item 3). The
*   `scope: global` logical bucket names of the app: the names the org admin consents to with
*   `sharedBucketConsent: true` on {@link ACCEPT_APP_ADOPTION_ROUTE}. Each such bucket sits in the
*   control plane project, and every org that accepted the app shares it. core-api answers the union
*   of two sources, in byte order, each name once: the list that the control plane's app offer
*   carried (`AppOffer.sharedBlobBuckets` of `@zion/sdk-cp-dp-contracts`), and the `scope: global`
*   buckets of the app's synced manifests. That union is the set the accept counts. A row with no
*   key asks no consent at accept, except in two cases:
*   1. core-api cannot read the app's manifest. The accept then answers 409
*      `blob_bucket_locations_unknown`.
*   2. The union holds more than 16 names, which breaks the grammar. core-api omits the key on that
*      row and logs a warning that names the app. The accept still asks for
*      `sharedBucketConsent: true` and answers 400 `blob_bucket_shared_consent_required` without it.
* - `orgBlobBuckets`: optional (2026-10-13-blob-buckets R-119 item 2). The `scope: org`,
*   `locality: central` logical bucket names of the app's synced manifests: the keys that the
*   `bucketLocations` of {@link ACCEPT_APP_ADOPTION_ROUTE} may name. The values it may name come from
*   {@link GET_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_ROUTE}. core-api omits the key when the app
*   declares no such bucket, or when the manifest has not reached the data plane or is unreadable.
*
* Both lists take one grammar: 1 to `BLOB_BUCKETS_PER_APP_MAX` (16) names, each in
* `blobBucketNameSchema` of `@zion/sdk-contracts`, distinct and sorted ascending in byte order, the
* grammar of `AppOffer.sharedBlobBuckets`. The empty list is refused, so an absent key is the one form
* of "none". Five adoption routes answer the same row with both lists (R-119 item 1): the list,
* and the row after each of accept, pause, resume and remove. The sixth route,
* {@link GET_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_ROUTE}, answers `{ allowedLocations }` and no row.
*
* A row that breaks one member's grammar fails as a whole, each list included: an empty list, 17
* names, a name outside the grammar, a repeated name (message `duplicate logical bucket name`) or an
* unsorted list (message `logical bucket names must be sorted in byte order`), each at
* `['sharedBlobBuckets', ...]` or `['orgBlobBuckets', ...]`.
*/
const appAdoptionSchema = z.object({
	appId: z.uuid(),
	appSlug: z.string().min(1),
	displayName: z.string().min(1),
	state: appAdoptionStateSchema,
	standing: z.boolean(),
	licenceRetracted: z.boolean(),
	updatedAt: z.iso.datetime(),
	sharedBlobBuckets: logicalBucketNamesSchema.optional(),
	orgBlobBuckets: logicalBucketNamesSchema.optional()
});
/** The 200 body of {@link LIST_APP_ADOPTIONS_ROUTE}: every row, unpaged. */
const listAppAdoptionsResponseSchema = z.array(appAdoptionSchema);
/**
* The 200 body of each write route: the app's row after the action, `changed` or `unchanged`, with
* the two bucket name lists of the list row (2026-10-13-blob-buckets R-119 item 1).
*/
const appAdoptionActionResponseSchema = appAdoptionSchema;
/**
* The 200 body of {@link GET_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_ROUTE} (2026-10-13-blob-buckets
* R-119 item 4): `{ allowedLocations }`. `allowedLocations` is a list in `allowedLocationsSchema` of
* `@zion/sdk-contracts` (1 to 16 distinct entries, each a single region in `dataLocationSchema`), in
* the stored order, or `null` when the data plane holds no list. The key is required: `null` is the
* one form of "no list". An empty list, a repeated entry, `us` or `eu`, or 17 entries fail under
* `allowedLocations`. A response schema, so `z.object` strips an unknown key (R-28). No
* `.transform()`. Pure.
*/
const appAdoptionBlobBucketLocationsResponseSchema = z.object({ allowedLocations: allowedLocationsSchema.nullable() });
/** The four refusal codes of the write routes, in the order of the module document above. */
const APP_ADOPTION_ERROR_CODES = Object.freeze([
	"app_adoption_not_found",
	"app_licence_retracted",
	"app_standing",
	"app_adoption_state_conflict"
]);
/**
* Fill the path of one action's write route with one app id.
*
* Who calls it: the org admin app and core-api's tests.
*
* @param appId the app's `release.apps_catalog` uuid, as {@link appAdoptionSchema} `appId`
*   publishes it.
* @param action one of `accept`, `pause`, `resume` and `remove`.
* @returns the request path, for example `/v1/admin/app-adoptions/<uuid>/pause`.
* @throws a `ZodError` when `appId` is not a uuid, so `..`, a slash or an empty string never reaches
*   a URL. Pure.
*/
function adminAppAdoptionActionPath(appId, action) {
	return fillPath(APP_ADOPTION_ACTION_ROUTES[action].path, { appId }, { appId: z.uuid() });
}
/**
* Every route this file declares, folded into `ADMIN_CONTRACT_ROUTE_FAMILIES` under
* `app-adoptions`: the list, then accept, pause, resume and remove, then the locations read of
* R-119 item 4, appended last so every earlier route keeps its position in the fold.
*/
const appAdoptionsRoutes = Object.freeze([
	LIST_APP_ADOPTIONS_ROUTE,
	ACCEPT_APP_ADOPTION_ROUTE,
	PAUSE_APP_ADOPTION_ROUTE,
	RESUME_APP_ADOPTION_ROUTE,
	REMOVE_APP_ADOPTION_ROUTE,
	GET_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_ROUTE
]);
//#endregion
export { ACCEPT_APP_ADOPTION_ROUTE, ADMIN_APP_ADOPTIONS_PATH, ADMIN_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_PATH, ADMIN_APP_ADOPTION_PATH_TEMPLATE, APP_ADOPTION_ACTION_ROUTES, APP_ADOPTION_ERROR_CODES, GET_APP_ADOPTION_BLOB_BUCKET_LOCATIONS_ROUTE, LIST_APP_ADOPTIONS_ROUTE, PAUSE_APP_ADOPTION_ROUTE, REMOVE_APP_ADOPTION_ROUTE, RESUME_APP_ADOPTION_ROUTE, adminAppAdoptionActionPath, appAdoptionAcceptBodySchema, appAdoptionActionBodySchema, appAdoptionActionResponseSchema, appAdoptionBlobBucketLocationsResponseSchema, appAdoptionSchema, appAdoptionsRoutes, listAppAdoptionsResponseSchema };

//# sourceMappingURL=app-adoptions.js.map